import {
  containsEssentialsEventDetailsPath,
  CustomerPropertiesMap,
  ExtendedPathwayOption,
  getPathwayOptionToRedirectUrlMapping,
  INTERNATIONAL_DISABLED_PATHWAYS,
  PATHWAY_TO_PROGRAM_CUSTOMER_PROPERTY_MAP,
  PathwaysRedirectUrls,
  TEEN_ENABLED_PATHWAYS,
} from '@lyrahealth-inc/shared-app-logic'

import { LOCAL_STORAGE_KEY } from '../constants/appConstants'

/**
 * Checks for any disabled pathway options for teen and international users, updates the redirect url key in storage, and returns the new redirect url.
 */
export const updateRedirectPathForPathwayOptions = ({
  redirectUrl,
  isInternationalUser,
  isTeenUser,
  customerPropertiesMap,
  forkedTriageV2Enabled,
  updateStorage,
}: {
  redirectUrl: string
  isInternationalUser: boolean
  isTeenUser: boolean
  customerPropertiesMap?: CustomerPropertiesMap
  forkedTriageV2Enabled: boolean
  updateStorage: (url: string) => void
}) => {
  let newUrl = updateRedirectPathForTeen({
    redirectUrl,
    isTeenUser,
    forkedTriageV2Enabled: forkedTriageV2Enabled ?? false,
    updateStorage,
  })
  newUrl = updateRedirectPathForInternational({
    redirectUrl: newUrl,
    isInternationalUser: isInternationalUser ?? false,
    forkedTriageV2Enabled: forkedTriageV2Enabled ?? false,
    updateStorage,
  })
  newUrl = updateRedirectPathForProgramAvailability({
    redirectUrl: newUrl,
    customerPropertiesMap,
    isInternationalUser: isInternationalUser ?? false,
    forkedTriageV2Enabled: forkedTriageV2Enabled ?? false,
    updateStorage,
  })
  newUrl = updateRedirectPathForEssentialsEvent({ redirectUrl: newUrl })
  return newUrl
}

const updateRedirectPathForEssentialsEvent = ({ redirectUrl }: { redirectUrl: string }) => {
  if (
    redirectUrl === PathwaysRedirectUrls.ESSENTIALS_EVENTS &&
    window.localStorage.getItem(LOCAL_STORAGE_KEY.ESSENTIALS_EVENT_SLUG)
  ) {
    const slugEvent = window.localStorage.getItem(LOCAL_STORAGE_KEY.ESSENTIALS_EVENT_SLUG)
    window.localStorage.removeItem(LOCAL_STORAGE_KEY.ESSENTIALS_EVENT_SLUG)
    return `${redirectUrl}/${slugEvent}`
  }
  return redirectUrl
}

/**
 * Updates the url key in storage and returns the new redirect url. Override pathways options that aren't enabled for teens with the self search pathway
 */
const updateRedirectPathForTeen = ({
  redirectUrl,
  isTeenUser,
  forkedTriageV2Enabled,
  updateStorage,
}: {
  redirectUrl: string
  isTeenUser: boolean
  forkedTriageV2Enabled: boolean
  updateStorage: (url: string) => void
}) => {
  const urlMapping = getPathwayOptionToRedirectUrlMapping(forkedTriageV2Enabled)
  const teenEnabledPaths = TEEN_ENABLED_PATHWAYS.map((pathway) => urlMapping[pathway]).filter(
    (item) => item !== undefined,
  ) as string[]
  if (isTeenUser && !teenEnabledPaths.includes(redirectUrl)) {
    if (redirectUrl === PathwaysRedirectUrls.ESSENTIALS_EVENTS || containsEssentialsEventDetailsPath(redirectUrl)) {
      redirectUrl = PathwaysRedirectUrls.ESSENTIALS
    } else {
      redirectUrl = PathwaysRedirectUrls.SELF_TRIAGE
    }
    updateStorage(redirectUrl)
  }
  return redirectUrl
}

/**
 * Blocks user from being able to access disabled care options for international users. Updates the redirect url key in storage and returns the new redirect url. These disabled care options should be hidden on the microsite forked triage page but sometimes we may have inaccurate location information (user has vpn or is traveling) and they may be able to access the disabled care options.
 */
const updateRedirectPathForInternational = ({
  redirectUrl,
  isInternationalUser,
  forkedTriageV2Enabled,
  updateStorage,
}: {
  redirectUrl: string
  isInternationalUser: boolean
  forkedTriageV2Enabled: boolean
  updateStorage: (url: string) => void
}) => {
  const urlMapping = getPathwayOptionToRedirectUrlMapping(forkedTriageV2Enabled)
  const disabledPaths = INTERNATIONAL_DISABLED_PATHWAYS.map((pathway) => urlMapping[pathway]).filter(
    (item) => item !== undefined,
  ) as string[]
  if (isInternationalUser && disabledPaths.includes(redirectUrl)) {
    redirectUrl = PathwaysRedirectUrls.SELF_TRIAGE
    updateStorage(redirectUrl)
  }
  return redirectUrl
}

const updateRedirectPathForProgramAvailability = ({
  redirectUrl,
  customerPropertiesMap,
  forkedTriageV2Enabled,
  updateStorage,
}: {
  redirectUrl: string
  customerPropertiesMap?: CustomerPropertiesMap
  isInternationalUser: boolean
  forkedTriageV2Enabled: boolean
  updateStorage: (url: string) => void
}) => {
  const urlMapping = getPathwayOptionToRedirectUrlMapping(forkedTriageV2Enabled)
  for (const [pathway, url] of Object.entries(urlMapping)) {
    if (redirectUrl === url) {
      const programCustomerProps = PATHWAY_TO_PROGRAM_CUSTOMER_PROPERTY_MAP.get(pathway as ExtendedPathwayOption)
      if (programCustomerProps && customerPropertiesMap) {
        const isAvailable = programCustomerProps.some(
          (property) =>
            property in customerPropertiesMap &&
            customerPropertiesMap[property as keyof CustomerPropertiesMap] === 'true',
        )
        if (!isAvailable) {
          redirectUrl = PathwaysRedirectUrls.SELF_TRIAGE
          updateStorage(redirectUrl)
          break
        }
      }
    }
  }
  return redirectUrl
}
